"use client";
import { useState, useEffect, useCallback, startTransition } from "react";
import { usePathname } from "next/navigation";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";

import { loginReduxState, logoutReduxState } from "@/src/store/auth";
import { setFavoriteList, handleListingFavorited } from "@/src/store/favorite";

/* import { setCookie } from "helpers/cookie"; */
import { Ga4WithEventParameters } from "src/helpers/google-tag-manager/events/ga4-with-eventparameters";
import { getToken } from "helpers/auth";
import { isListingUrlControl } from "helpers/url";
import { clearUserSessionCookies, setUserSessionCookies } from "@/src/helpers/user-session-cookies";
import { deleteCookie } from "@/src/helpers/cookie";
import { GTMPushEvent } from "@/src/helpers/google-tag-manager";
import { gtmLoadInterval } from "helpers/google-tag-manager/gtm-load-interval";

import { useSetEnv } from "@/src/utils/setEnv";

import headerMock, { headerDisabledRoutes } from "mock/header";
import { HASHED_MAIL } from "@/src/utils/constants/cookie-keys";
import { getActiveBanner, BANNER_TYPES } from "@/constants/main-page-banners";

import IconLogo from "assets/svg/logo.svg";
import IconArrow from "assets/svg/chevron-down.svg";

import HeaderMenu from "components/header-menu";
import HeaderMenuSub from "components/header-menu-sub";
import Button from "components/common/button";
import Loading from "components/common/loading-spinner";

import { headerMembershipButton } from "@/constants/test-selector-ids";
import { runIdle } from "@/src/utils/idle-utils";

import styles from "./styles.module.css";

const Header = ({ categoryTree }) => {
  useSetEnv();
  const pathname = usePathname();
  const dispatch = useDispatch();

  const isAuthenticated = useSelector((state) => state.auth.isAuth);

  const [isUserMenuOpened, setIsUserMenuOpened] = useState(false);
  const [isAuth, setIsAuth] = useState(false);
  const [userData, setUserData] = useState();
  const [loading, setLoading] = useState(true);

  const isHomePage = pathname === "/";
  const isStickyControl = isListingUrlControl(pathname);

  const isHeaderHidden = headerDisabledRoutes.some((route) => pathname.startsWith(route));
  if (isHeaderHidden) return null;

  const handleTriggerSubMenu = (e, directive) => {
    e.stopPropagation();
    startTransition(() => {
      setIsUserMenuOpened(!directive ? !isUserMenuOpened : false);
    });
  };

  const logoutHeader = () => {
    setIsAuth(false);
    setUserData(undefined);
    setIsUserMenuOpened(false);

    runIdle(() => {
      try {
        Ga4WithEventParameters({
          eventName: "header",
          eventParameters: { label: "click||profil||cikis-yap" },
        });

        deleteCookie(HASHED_MAIL);
        localStorage.removeItem("user-data");
        localStorage.removeItem("Authorization");
        localStorage.removeItem("RefreshToken");
        localStorage.removeItem("related-login-sent");

        dispatch(logoutReduxState());
        dispatch(setFavoriteList([]));
        dispatch(handleListingFavorited(false));
      } catch (error) {
        console.error("Error during logout cleanup:", error);
      }
    }, 1000);
  };

  const handleSetGa4Event = () => {
    const conditionalLabelForUserIsAuth = isAuth ? "panele-git" : "ucretsiz-ilan-ver";
    runIdle(() => {
      Ga4WithEventParameters({
        eventName: "header",
        eventParameters: { label: `click||${conditionalLabelForUserIsAuth}` },
      });
    }, 1000);
  };

  const gtmPushRelated = useCallback((userId) => {
    if (typeof window === "undefined" || !userId) return;

    const relatedLoginStatus = window.localStorage.getItem("related-login-sent") === "true";
    if (relatedLoginStatus) return;

    return gtmLoadInterval(() => {
      GTMPushEvent({
        event: "related_event",
        event_name: "related_login",
        event_parameters: {
          user_id: userId,
        },
      });
      window.localStorage.setItem("related-login-sent", "true");
    });
  }, []);

useEffect(() => {
  if (typeof window === "undefined") {
    setLoading(false);
    return;
  }

  const storedUser = window.localStorage.getItem("user-data");

  if (storedUser) {
    try {
      const parsedUser = JSON.parse(storedUser);
      setUserData(parsedUser);
      setIsAuth(true);
      dispatch(loginReduxState());
    } catch (error) {
      console.error("Error parsing user data from storage.");
    }
  }
  setLoading(false);
}, [dispatch]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const rawUserData = window.localStorage.getItem("user-data");
    let parsedUserData = null;

    if (rawUserData) {
      try {
        parsedUserData = JSON.parse(rawUserData);
      } catch (error) {
        console.error("Error parsing user data from storage.");
      }
    }

    setUserData(parsedUserData || undefined);
    setIsAuth(isAuthenticated);

    if (isAuthenticated && parsedUserData) {
      setUserSessionCookies(parsedUserData);
    } else {
      clearUserSessionCookies();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuth) return;

    const isLoginRoute = pathname === "/giris" || pathname === "/giris/";
    if (isLoginRoute || !userData?.id) return;

    runIdle(() => {
      gtmPushRelated(userData.id);
    }, 1000);
  }, [gtmPushRelated, isAuth, pathname, userData]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#cikis") {
      logoutHeader();
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [pathname]);

  const headerClasses = classNames({
    [styles.wrapper]: true,
    [styles.wrapperIsSticky]: isStickyControl,
  });

  const activeBanner = getActiveBanner();

  const renderBanner = () => {
    if (!activeBanner) return null;

    const bannerImage = <img src={activeBanner.imageSource} alt={activeBanner.alt} className={styles.bannerImage} />;

    if (activeBanner.type === BANNER_TYPES.LINK && activeBanner.link) {
      return (
        <a target="_blank" href={activeBanner.link} rel="noreferrer" className={styles.bannerLink}>
          {bannerImage}
        </a>
      );
    }

    return bannerImage;
  };

  return (
    <>
      {isHomePage && <div className={styles.bannerWrapper}>{renderBanner()}</div>}
      <header className={headerClasses}>
        <div className={styles.inner}>
          <a href="/" className={styles.logoWrap}>
            <IconLogo />
          </a>
          <HeaderMenu categoryTree={categoryTree} />
        </div>
        {loading ? (
          <div className={styles.loading}>
            <Loading loadingClass={styles.loadingClass} />
          </div>
        ) : (
          <div className={styles.actionWrap}>
            <div className={styles.subscriptionWrap}>
              <a
                role="button"
                onClick={(e) => handleTriggerSubMenu(e)}
                onBlur={(e) => {
                  handleTriggerSubMenu(e, "close");
                }}
                className={classNames({
                  [styles.highlighter]: true,
                  [styles.highlighterForUser]: isAuth,
                })}
                data-test-selector={headerMembershipButton}
              >
                {isAuth ? userData?.fullName : "Üyelik"}
                <span
                  className={classNames({
                    [styles.icon]: true,
                    [styles.iconTransformed]: isUserMenuOpened,
                    [styles.iconDark]: isAuth,
                  })}
                >
                  <IconArrow />
                </span>
              </a>
              <HeaderMenuSub
                isOpen={isUserMenuOpened}
                isUserMenu={!isAuth}
                logout={logoutHeader}
                data={headerMock[isAuth ? "authUser" : "subscription"]}
                isAuth={isAuth}
              />
            </div>
            <div className={styles.buttonWrap}>
              <a
                href={
                  isAuth
                    ? `${window.env.EJ_APPS_PANEL_HOSTNAME}/#/member/authorize?token=${getToken()}`
                    : "/uye-ol/bireysel"
                }
                target={isAuth ? "_blank" : "_self"}
                rel="noreferrer"
              >
                <Button size="M" onClick={handleSetGa4Event}>
                  {isAuth && userData?.accountType === "CORPORATE" ? "Panele Git" : "Ücretsiz İlan Ver"}
                </Button>
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

Header.propTypes = {
  categoryTree: PropTypes.array.isRequired,
  isAuth: PropTypes.bool,
};

export default Header;
